import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faBalanceScale } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { debounce } from "debounce";
import * as notify from "../../../shared/notify";

import { NumberField, Select } from "../../form-inputs/inputs";

import { DEBOUNCE_DELAY, getUomList, getUomWeightList } from "../lib/constants";
import { checkOptionsForUOM } from "../lib/helpers";
import { useMeasurements } from "components/shipment-editor/measurements/hooks/use-measurements";

const MeasurementLine = ({ editOnly, sourcePage, data, type, isSelected, selectMeasurementRow, disabled }) => {
    const { measurementState, saveMeasurement, deleteSingleMeasurementWipKey, deleteMeasurementsWipKeys } = useMeasurements();
    const isCompleted = measurementState.isCompleted;
    const [selectedUom, setSelectedUom] = useState({ height: data.height.uom, width: data.width.uom, depth: data.depth.uom });
    const [selectedWeightUom, setSelectedWeightUom] = useState(data.weight.uom);
    const [measurementData, setMeasurementData] = useState({
        key: data.key,
        height: data.height.value,
        width: data.width.value,
        depth: data.depth.value,
        weight: data.weight.value,
    });
    const [hasUpdate, setHasUpdate] = useState(false);

    useEffect(() => {
        if (hasUpdate) {
            debouncedUpdateMeasurementValue(measurementData, selectedUom, selectedWeightUom, sourcePage);
        }
    }, [measurementData, hasUpdate, selectedUom, selectedWeightUom]);

    useEffect(() => {
        setMeasurementData({ key: data.key, height: data.height.value, width: data.width.value, depth: data.depth.value, weight: data.weight.value });
        setHasUpdate(false);
    }, [data]);

    const updateMeasurmentValue = (key, value) => {
        const newMeasurementData = { ...measurementData, [key]: parseFloat(value) };

        setMeasurementData(newMeasurementData);
        setHasUpdate(true);
    };

    const buildMeasurement = (newData, newUom, newWeightUom) => {
        return {
            ...data,
            key: newData.key,
            height: { value: newData.height, uom: newUom.height },
            width: { value: newData.width, uom: newUom.width },
            depth: { value: newData.depth, uom: newUom.depth },
            weight: { value: newData.weight, uom: newWeightUom },
        };
    };

    const debouncedUpdateMeasurementValue = useCallback(
        debounce(async (newData, newUom, newWeightUom) => {
            try {
                const hasSaved = await saveMeasurement(measurementState, buildMeasurement(newData, newUom, newWeightUom), !editOnly, sourcePage);

                if (hasSaved) {
                    notify.success("Saved");
                }
            } catch (ex) {}
            setHasUpdate(false);
        }, DEBOUNCE_DELAY),
        []
    );

    const trash = async (e) => {
        e.preventDefault();

        if (measurementState.measuringWipKey) {
            await deleteSingleMeasurementWipKey(measurementState, data.key);
        } else {
            await deleteMeasurementsWipKeys(measurementState, data.key);
        }

        selectMeasurementRow(null);
    };
    const zeroWeight = (e) => {
        e.preventDefault();
        updateMeasurmentValue("weight", 0);

        selectRow();
    };
    const selectRow = () => {
        if (!isSelected) {
            selectMeasurementRow(data);
        }
    };
    const fieldChange = (name, value) => {
        const splitName = name.split("_");
        updateMeasurmentValue(splitName.length === 2 ? splitName[1] : name, value);
    };

    const containerClasses = classNames("measurement-row", { "-selected": isSelected });
    const weightUom = checkOptionsForUOM(getUomWeightList(data.positionValue)[type], selectedWeightUom);

    return (
        <div
            id={`measurement_line_${data.key.join("-")}`}
            className={containerClasses}
        >
            <div className="input-group -right">
                <NumberField
                    value={measurementData.height}
                    step={0.01}
                    min={0.01}
                    required
                    label="height"
                    onFocus={selectRow}
                    name={`x${data.key.join("-")}_height`}
                    updateOnChange={fieldChange}
                    disabled={isCompleted || disabled}
                />
                <Select
                    excludeSelect={true}
                    label=" "
                    name={`x${data.key.join("-")}_heightUom`}
                    options={checkOptionsForUOM(getUomList(data.positionValue)[type], selectedUom.height)}
                    onFocus={selectRow}
                    value={selectedUom.height}
                    disabled={isCompleted || disabled}
                    onChange={(e) => {
                        setHasUpdate(true);
                        setSelectedUom({ ...selectedUom, height: e.currentTarget.value });
                    }}
                ></Select>
                <NumberField
                    step={0.01}
                    min={0.01}
                    value={measurementData.width}
                    required={true}
                    label="width"
                    onFocus={selectRow}
                    name={`x${data.key.join("-")}_width`}
                    updateOnChange={fieldChange}
                    disabled={isCompleted || disabled}
                />
                <Select
                    excludeSelect={true}
                    label=" "
                    name={`x${data.key.join("-")}_widthUom`}
                    options={checkOptionsForUOM(getUomList(data.positionValue)[type], selectedUom.width)}
                    onFocus={selectRow}
                    value={selectedUom.width}
                    disabled={isCompleted || disabled}
                    onChange={(e) => {
                        setHasUpdate(true);
                        setSelectedUom({ ...selectedUom, width: e.currentTarget.value });
                    }}
                ></Select>
                <NumberField
                    step={0.01}
                    min={0.01}
                    value={measurementData.depth}
                    required={true}
                    label="depth"
                    onFocus={selectRow}
                    name={`x${data.key.join("-")}_depth`}
                    updateOnChange={fieldChange}
                    disabled={isCompleted || disabled}
                />
                <Select
                    excludeSelect={true}
                    label=" "
                    name={`x${data.key.join("-")}_depthUom`}
                    options={checkOptionsForUOM(getUomList(data.positionValue)[type], selectedUom.depth)}
                    onFocus={selectRow}
                    value={selectedUom.depth}
                    disabled={isCompleted || disabled}
                    onChange={(e) => {
                        setHasUpdate(true);
                        setSelectedUom({ ...selectedUom, depth: e.currentTarget.value });
                    }}
                ></Select>
                <NumberField
                    value={measurementData.weight}
                    required={true}
                    label="weight"
                    onFocus={selectRow}
                    name={`x${data.key.join("-")}_weight`}
                    updateOnChange={fieldChange}
                    disabled={isCompleted || disabled}
                />
                <Select
                    excludeSelect={true}
                    label=" "
                    name={`x${data.key.join("-")}_weightUom`}
                    options={weightUom}
                    onFocus={selectRow}
                    value={selectedWeightUom}
                    disabled={isCompleted || disabled}
                    onChange={(e) => {
                        setHasUpdate(true);
                        setSelectedWeightUom(e.currentTarget.value);
                    }}
                ></Select>
                {!isCompleted && !editOnly && (
                    <button
                        className="button -small"
                        onClick={trash}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                )}
                {!isCompleted && !editOnly && (
                    <button
                        className="button -small"
                        onClick={zeroWeight}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faBalanceScale} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default MeasurementLine;
